import { Center, SkeletonText, Spinner, Table, useDisclosure } from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import TabsList from "../../components/Common/TabsList"
import { RolesOrderBy } from "../../client"
import { lazy, Suspense, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import Sort from "../../components/Common/Sort"
import ActionsMenu from "../../components/Common/ActionsMenu"
import Navbar from "../../components/Common/Navbar"
import { userHasPermission } from "../../utils"
import { NoPermission } from "../../components/UI/no-permission"
import { readRoles } from "../../api/roles"

const DetailRole = lazy(() => import("../../components/Roles/DetailRole"))
const AddRole = lazy(() => import("../../components/Roles/AddRole"))

export const Route = createFileRoute("/_layout/roles")({
  component: Roles
})


function RolesTable() {
  // Permission check
  const [ro_create, ro_read, ro_update, ro_delete] = useMemo(() => {
    return ["create", "read", "update", "delete"].map(
      (action) => userHasPermission([{ perm: `roles_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!ro_read) return <NoPermission/>

  const fields = [
    { title: "Rolle", col: "name", align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  const [searchValue, setSearchValue] = useState<string>("")

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value)
  }

  // Sorting
  const [columnName, setColumnName] = useState<RolesOrderBy>()
  const [orderBy, setOrderBy] = useState<string>("")
  const [isDesc, setIsDesc] = useState<boolean>()

  // Query
  const queryOptions = useMemo(() =>
    readRoles({
      order_by: columnName,
      descending: isDesc,
      query: searchValue,
    }),
  [columnName, isDesc, searchValue])

  const { data: roles, isPending, isPlaceholderData} = useQuery({
    ...queryOptions,
    placeholderData: (prevData) => prevData,
  })

  // Sorting
  const sortTable = (name: RolesOrderBy) => {
    if (columnName !== name) {
      setColumnName(name)
      setOrderBy("")
      sorting("")
    } else {
      sorting(orderBy)
    }
  }

  const sorting = (order: string) => {
    switch (order) {
      case "":
        setOrderBy("desc")
        setIsDesc(true)
        break
      case "desc":
        setOrderBy("asc")
        setIsDesc(false)
        break
      case "asc":
        setOrderBy("")
        setIsDesc(undefined)
        setColumnName(undefined)
        break
    }
  }

    // Open media detail view
    const detailModal = useDisclosure()
    const [roleId, setRoleId] = useState("")

    const openModal = (id: string) => {
      setRoleId(id)
      detailModal.onOpen()
    }


  return (
    <>
      <Navbar
        type="Rolle"
        addAllowed={ro_create}
        addModalAs={AddRole}
        showSearch={true}
        onSearchChange={handleSearchValueChange}
      />

      {detailModal.open &&
        <Suspense fallback={
          <Center
            bg="rgba(0, 0, 0, 0.3)"
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            zIndex="10000"
          >
            <Spinner size="lg" color="white" />
          </Center>}
        >
          <DetailRole
            isOpen={detailModal.open}
            onClose={detailModal.onClose}
            roleId={roleId}
          />
        </Suspense>
      }

      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader
                    key={i}
                    onClick={() => sortTable(field.col as RolesOrderBy)}
                    cursor="pointer"
                    textAlign={field.align}
                  >
                    {field.title}
                    {columnName === field.col &&
                      <Sort
                        columnNamePassed={columnName}
                        orderByPassed={orderBy}
                      />
                    }
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader key={i} textAlign={field.align}>
                    {field.title}
                  </Table.ColumnHeader>
                ),
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(2).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!roles?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={2} textAlign="center">
                    Keine Rollen verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                roles?.data.map((role, i) => (
                  <Table.Row key={i} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell onClick={() => openModal(role.id)} cursor="pointer">
                      {role.name}
                    </Table.Cell>

                    <Table.Cell textAlign="right">
                      <ActionsMenu
                        type="Rolle"
                        value={role}
                        disableEdit={!ro_update}
                        disableDelete={!ro_delete}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>
    </>
  )
}

export default RolesTable

function Roles() {
  const tabs = useMemo(() => [
    {value: "users", title: "Benutzer"},
    {value: "roles", title: "Rollen"},
    {value: "permissions", title: "Berechtigungen"},
  ], [])

  return (
    <TabsList tabs={tabs} defaultVal="users" />
  )
}
